import { SimilarFilesResponse, fetchSimilarAssetsByUploadedFiles } from "@/api";
import { ONE_DAY_MILLISECONDS } from "@/constants";
import { resizeImageFile } from "@/utils/misc";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  AssetDto,
  UploadWithAssetsDto,
  UpdateRelatedAssetMetadataDto,
  RelatedAssetMetadataDto,
  DefaultApiAssetsControllerUploadRelatedAssetRequest,
  DefaultApiAssetsControllerRemoveRelatedAssetRequest,
  AssetWithRelatedMetadataDto,
  UpdateDocumentMetadataDto,
  DocumentMetadataDto,
} from "generated";
import { useCallback, useState } from "react";
import api from "../services/api/axios-client";
import {
  ErrorResponse,
  MetadataUpdatingStatusParams,
  UpdatingMetadata,
} from "./types";
import { AxiosResponse } from "axios";
import { useShallow } from "zustand/react/shallow";
import { useCurrentUploadId, useOptionalUploadId } from "./uploads";
import { useStore } from "@/services/store";
import { AssetsChangeMap } from "@/services/store/slices/mandatoryMetaData/types";
import { MandatoryVideoMetaData } from "@/services/store/slices/videoMetadata/types";

export function useAssetFileQuery(id: number, thumbnail = true) {
  return useQuery({
    queryKey: ["assets", "file", id, thumbnail],
    queryFn: () =>
      api
        .assetsControllerGetFile(
          { assetId: id, th: thumbnail },
          { responseType: "blob" },
        )
        .then((r) => ({
          ...r,
          id,
          blobUrl: URL.createObjectURL(r.data as Blob),
        })),
    staleTime: ONE_DAY_MILLISECONDS,
    meta: {
      onRemoveFromCache: ({ data }: { data: { blobUrl: string } }) => {
        URL.revokeObjectURL(data.blobUrl);
      },
    },
  });
}

export function useRemoveAssetMutation() {
  const queryClient = useQueryClient();
  const { removeAsset } = useStore(
    useShallow((state) => ({ removeAsset: state.removeAsset })),
  );

  return useMutation({
    mutationFn: (id: number) => {
      return api.assetsControllerRemove({ id });
    },
    onSuccess(deleteResponse) {
      removeAsset(deleteResponse.data.id);
      queryClient.setQueryData(
        ["uploads", deleteResponse.data.uploadId],
        (cachedData: UploadWithAssetsDto) => {
          return {
            ...cachedData,
            assets: cachedData.assets.filter(
              (asset) => asset.id !== deleteResponse.data.id,
            ),
          };
        },
      );
    },
  });
}

export const useVideoMetadataMutation = (assetId: number) => {
  const uploadId = useOptionalUploadId();
  const queryClient = useQueryClient();
  const setVideoMetaData = useStore((state) => state.setVideoMetaData);
  return useMutation({
    mutationFn: (
      updateRelatedAssetMetadataDto: UpdateRelatedAssetMetadataDto,
    ) => {
      return api.assetsControllerUpdateRelatedAssetMetadata({
        assetId,
        updateRelatedAssetMetadataDto,
      });
    },
    onSuccess: (response: AxiosResponse<RelatedAssetMetadataDto>) => {
      const filteredMetadata: Partial<MandatoryVideoMetaData> =
        Object.fromEntries(
          Object.entries(response.data).filter(([, value]) => value !== null),
        );
      setVideoMetaData(assetId, filteredMetadata);

      queryClient.invalidateQueries({ queryKey: ["uploads", uploadId] });
    },
  });
};

export const useDocumentMetadataMutation = (assetId: number) => {
  const uploadId = useOptionalUploadId();
  const queryClient = useQueryClient();
  const setDocumentMetaData = useStore((state) => state.setDocumentMetaData);
  return useMutation({
    mutationFn: (updateDocumentMetadataDto: UpdateDocumentMetadataDto) => {
      return api.assetsControllerUpdateDocumentMetadata({
        assetId,
        updateDocumentMetadataDto,
      });
    },
    onSuccess: (response: AxiosResponse<DocumentMetadataDto>) => {
      setDocumentMetaData(assetId, {
        title: response.data.title ?? null,
        isCompliant: response.data.isCompliant ?? false,
      });
      queryClient.setQueryData(
        ["uploads", uploadId],
        (cachedData: UploadWithAssetsDto) => ({
          ...cachedData,
          assets: cachedData.assets.map((asset) =>
            asset.id === assetId
              ? { ...asset, documentMetadata: response.data }
              : asset,
          ),
        }),
      );
    },
  });
};

export const useUploadRelatedAssetMutation = (assetId: number) => {
  const uploadId = useOptionalUploadId();
  const queryClient = useQueryClient();
  const { asset, setRelatedAssets } = useStore(
    useShallow((state) => ({
      asset: state.videoAssets[assetId],
      setRelatedAssets: state.setRelatedAssets,
    })),
  );
  return useMutation({
    mutationFn: (req: DefaultApiAssetsControllerUploadRelatedAssetRequest) => {
      return api.assetsControllerUploadRelatedAsset(req);
    },
    onSuccess: (response) => {
      queryClient.setQueryData(
        ["uploads", uploadId],
        (cachedData: UploadWithAssetsDto) => ({
          ...cachedData,
          assets: cachedData.assets.map((asset: AssetWithRelatedMetadataDto) =>
            asset.id === assetId
              ? {
                  ...asset,
                  relatedAssetMetadata: {
                    ...asset.relatedAssetMetadata,
                    relatedAssets: [
                      ...asset.relatedAssetMetadata!.relatedAssets,
                      response.data,
                    ],
                  },
                }
              : asset,
          ),
        }),
      );

      setRelatedAssets(assetId, [...asset.relatedAssets, response.data]);
    },
  });
};

export const useRemoveRelatedAssetMutation = (assetId: number) => {
  const uploadId = useCurrentUploadId();
  const queryClient = useQueryClient();
  const { asset, setRelatedAssets } = useStore(
    useShallow((state) => ({
      asset: state.videoAssets[assetId],
      setRelatedAssets: state.setRelatedAssets,
    })),
  );
  return useMutation({
    mutationFn: (req: DefaultApiAssetsControllerRemoveRelatedAssetRequest) => {
      return api.assetsControllerRemoveRelatedAsset(req);
    },
    onSuccess: (_, variables) => {
      const updatedRelatedAssets = asset.relatedAssets.filter(
        (a) => a.id !== variables.relatedAssetId,
      );

      setRelatedAssets(assetId, updatedRelatedAssets);

      queryClient.setQueryData(
        ["uploads", uploadId],
        (cachedData: UploadWithAssetsDto) => ({
          ...cachedData,
          assets: cachedData.assets.map((a) =>
            a.id === assetId
              ? {
                  ...a,
                  relatedAssetMetadata: {
                    ...a.relatedAssetMetadata,
                    relatedAssets: updatedRelatedAssets,
                  },
                }
              : a,
          ),
        }),
      );
    },
  });
};

const updateAssets = async (assets: AssetsChangeMap): Promise<AssetDto> => {
  const result = await Promise.allSettled(
    Object.entries(assets).map(([assetId, asset]) =>
      api.assetsControllerUpdate({
        id: Number(assetId),
        updateAssetDto: { ...asset },
      }),
    ),
  );
  const firstFulfilled = result.find((r) => r.status === "fulfilled");
  if (firstFulfilled) return firstFulfilled.value.data;
  throw new Error("No assets were updated successfully");
};

export const useUpdateDocumentAssetMutation = (assetId: number) => {
  const uploadId = useOptionalUploadId();
  const queryClient = useQueryClient();
  const setDocumentMetaData = useStore((state) => state.setDocumentMetaData);

  return useMutation({
    mutationFn: updateAssets,
    onSuccess(responseData: AssetDto) {
      const { originalName, language, contentType } = responseData;

      setDocumentMetaData(assetId, {
        originalName,
        language,
        contentType: contentType ?? null,
      });

      queryClient.setQueryData(
        ["uploads", uploadId],
        (cachedData: UploadWithAssetsDto) => ({
          ...cachedData,
          assets: cachedData.assets.map((asset) =>
            asset.id === responseData.id
              ? { ...asset, ...responseData }
              : asset,
          ),
        }),
      );
    },
  });
};

export const useUpdateVideoAssetMutation = (assetId: number) => {
  const queryClient = useQueryClient();
  const uploadId = useCurrentUploadId();
  const { setVideoMetaData, asset } = useStore(
    useShallow((state) => ({
      setVideoMetaData: state.setVideoMetaData,
      asset: state.videoAssets[assetId],
    })),
  );
  return useMutation({
    mutationFn: updateAssets,
    onSuccess(responseData: AssetDto) {
      const { originalName } = responseData;

      setVideoMetaData(assetId, { ...asset, originalName });

      queryClient.setQueryData(
        ["uploads", uploadId],
        (cachedData: UploadWithAssetsDto) => ({
          ...cachedData,
          assets: cachedData.assets.map((asset) =>
            asset.id === responseData.id
              ? { ...asset, ...responseData }
              : asset,
          ),
        }),
      );
    },
  });
};

export function useUpdateAssetsMutation() {
  const { setMetadataError, updateAssetMetadataSuccessful } = useStore(
    useShallow((state) => ({
      setMetadataError: state.setMetadataError,
      updateAssetMetadataSuccessful: state.updateAssetMetadataSuccessful,
    })),
  );
  const queryClient = useQueryClient();
  const [updatingMetadata, setUpdatingMetadata] =
    useState<UpdatingMetadata | null>(null);

  const result = useMutation({
    mutationFn: async (assets: AssetsChangeMap) => {
      return Promise.allSettled(
        Object.entries(assets).map(([assetId, asset]) => {
          return api.assetsControllerUpdate({
            id: Number(assetId),
            updateAssetDto: {
              ...asset,
            },
          });
        }),
      );
    },
    onSuccess(responseData, requestData) {
      const rejectedResult = responseData.filter(
        (result) => result.status === "rejected",
      );

      if (rejectedResult.length > 0) {
        const errorResponse = rejectedResult.reduce((acc, result) => {
          const { status, statusText } = result.reason.response;
          const { pathname } = new URL(result.reason.request.responseURL);
          acc[pathname] = {
            status,
            message: statusText,
            validationErrors:
              status === 400 ? result.reason.response.data.message : undefined,
          };

          return acc;
        }, {} as ErrorResponse);

        setMetadataError(errorResponse);
      }

      const fulfilledResponse = responseData
        .filter((res) => res.status === "fulfilled")
        .map((result) => result.value.data);

      if (fulfilledResponse.length > 0) {
        const responseLookup = fulfilledResponse.reduce(
          (agg, response) => {
            const asset = response;
            if (asset) {
              agg[asset.id] = asset;
            }
            return agg;
          },
          {} as Record<number, AssetDto>,
        );

        queryClient.setQueryData(
          ["uploads", fulfilledResponse[0]?.uploadId],
          (cachedData: UploadWithAssetsDto) => {
            return {
              ...cachedData,
              assets: cachedData.assets.map((cachedAsset) => {
                const updatedAsset = responseLookup[cachedAsset.id];
                return updatedAsset
                  ? { ...cachedAsset, ...updatedAsset }
                  : cachedAsset;
              }),
            };
          },
        );

        updateAssetMetadataSuccessful(Object.values(responseLookup));
      }

      setUpdatingMetadata(
        metadataUpdatingStatus({
          assets: requestData,
          currentState: updatingMetadata,
          isUpdating: false,
        }),
      );
    },
    onMutate: (assets) => {
      setUpdatingMetadata(
        metadataUpdatingStatus({
          assets,
          currentState: updatingMetadata,
          isUpdating: true,
        }),
      );
    },
  });
  return { updatingMetadata, ...result };
}

export function useSimilarAssets() {
  const [activeRequestsCount, setActiveRequestsCount] = useState(0);
  const [similarAssets, setSimilarAssets] = useState<SimilarFilesResponse>(
    {} as SimilarFilesResponse,
  );

  const checkForSimilarAssets = useCallback(async (files: File[]) => {
    try {
      setActiveRequestsCount((count) => count + 1);
      const resizedFiles = await Promise.all(
        files.map((file) => resizeImageFile(file, 380, 380)),
      );
      const similarityResponse =
        await fetchSimilarAssetsByUploadedFiles(resizedFiles);

      setSimilarAssets((prev) => ({ ...prev, ...similarityResponse }));
    } catch (error) {
      console.error("Error fetching similar files", error);
    } finally {
      setActiveRequestsCount((count) => count - 1);
    }
  }, []);

  return {
    loadingSimilarAssets: activeRequestsCount > 0,
    similarAssets,
    setSimilarAssets,
    checkForSimilarAssets,
  };
}

function metadataUpdatingStatus({
  currentState,
  assets,
  isUpdating,
}: MetadataUpdatingStatusParams) {
  const updatedState = { ...currentState };
  Object.entries(assets).forEach(([id, asset]) => {
    Object.keys(asset).forEach((key) => {
      const assetId = Number(id);
      if (!updatedState[assetId]) updatedState[assetId] = {};
      updatedState[assetId][key] = isUpdating;
    });
  });
  return updatedState;
}
